import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Lock, KeyRound, FileText } from 'lucide-react';
import { ENCRYPTION_STATEMENT } from './EncryptedBadge';

const ROWS = [
  {
    icon: <Lock className="w-4 h-4 text-emerald-600" aria-hidden="true" />,
    title: 'Personal data (email, name, username)',
    method: 'AES-128 (Fernet) encrypted at rest',
    badge: 'Encrypted',
  },
  {
    icon: <Lock className="w-4 h-4 text-emerald-600" aria-hidden="true" />,
    title: 'Financial data',
    subtitle: 'Investment amounts, valuations, prices, mortgages, interest rates and other monetary fields',
    method: 'AES-128 (Fernet) encrypted at rest',
    badge: 'Encrypted',
  },
  {
    icon: <KeyRound className="w-4 h-4 text-emerald-600" aria-hidden="true" />,
    title: 'Password',
    method: 'Argon2id one-way hash — never recoverable',
    badge: 'Hashed',
  },
  {
    icon: <FileText className="w-4 h-4 text-emerald-600" aria-hidden="true" />,
    title: 'Session token',
    method: 'JWT — short-lived, no third-party tracking cookies',
    badge: 'Token-based',
  },
];

const DataPrivacyCard = () => {
  return (
    <Card data-testid="data-privacy-card" className="border-emerald-100">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-600" aria-hidden="true" />
          <span>Your data is encrypted</span>
          <Badge
            data-testid="data-privacy-active-badge"
            className="ml-1 bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
          >
            Active
          </Badge>
        </CardTitle>
        <CardDescription>
          Industry-standard encryption protects your personal and financial data at rest.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-100">
          {ROWS.map((row) => (
            <li
              key={row.title}
              data-testid={`data-privacy-row-${row.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className="flex items-start gap-3 p-3 sm:p-4"
            >
              <span className="mt-0.5 shrink-0">{row.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium text-slate-900 text-sm">{row.title}</span>
                  <Badge
                    variant="secondary"
                    className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200"
                  >
                    {row.badge}
                  </Badge>
                </div>
                {row.subtitle && (
                  <p className="text-xs text-slate-500 mt-1">{row.subtitle}</p>
                )}
                <p className="text-xs text-slate-600 mt-1">{row.method}</p>
              </div>
            </li>
          ))}
        </ul>
        <div
          data-testid="data-privacy-statement"
          className="rounded-lg bg-emerald-50 border border-emerald-100 p-3 flex items-start gap-2"
        >
          <Lock className="w-4 h-4 mt-0.5 shrink-0 text-emerald-700" aria-hidden="true" />
          <p className="text-xs text-emerald-900 leading-relaxed">{ENCRYPTION_STATEMENT}</p>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          We never store your password in readable form. Even our engineers cannot read your
          encrypted data without your explicit approval.{' '}
          <Link
            data-testid="data-privacy-learn-more"
            to="/privacy-policy"
            className="font-medium text-emerald-700 hover:text-emerald-800 underline-offset-2 hover:underline"
          >
            Learn more
          </Link>
        </p>
      </CardContent>
    </Card>
  );
};

export default DataPrivacyCard;
